import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { useReadActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import useEventDetails from "../../hooks/useEventDetails";
import { OrgPersonSearchDocument } from "../graphql/__generated__/OrgPersonSearch.generated";
import { OrgPersonsByCalendarIdDocument } from "../graphql/__generated__/OrgPersonsByCalendarId.generated";

export type CalendarProfile = {
  primaryCalendar: string;
  profile: {
    givenName: string | null;
    familyName: string | null;
    externalImageUrl: string | null;
  } | null;
  isYou: boolean;
};

type QueryResponse = {
  calendarProfiles: CalendarProfile[];
  loading: boolean;
};

export const useCalendarProfilesByCalendarId = (
  orgId: string,
  selectedCalendarIds: string[],
): QueryResponse => {
  const { data, loading } = useQuery(OrgPersonsByCalendarIdDocument, {
    variables: {
      orgId: orgId,
      calendarIds: selectedCalendarIds || [],
    },
  });

  const org = getValue(data?.org, "Org");
  const orgPersonList = getValue(org?.orgPersonsByCalendarIdsErrorable, "OrgPersonList");

  const calendarProfiles = (orgPersonList?.list.map((a) => {
    return {
      primaryCalendar: a.primaryCalendarId,
      profile: a.profile,
      isYou: a.isYou,
    };
  }) || []) as CalendarProfile[];

  const calendarProfilesSortedByTheOrderOfSelectedCalendarIds = [...calendarProfiles].sort(
    (a, b) => {
      return (
        selectedCalendarIds.findIndex((v) => v == a.primaryCalendar) -
        selectedCalendarIds.findIndex((v) => v == b.primaryCalendar)
      );
    },
  );

  return { calendarProfiles: calendarProfilesSortedByTheOrderOfSelectedCalendarIds, loading };
};

const useCalendarProfilesFromSearch = (orgId: string, query: string): QueryResponse => {
  const { data, loading } = useQuery(OrgPersonSearchDocument, {
    variables: {
      orgRelayId: orgId,
      queryInput: query,
      excludeCalendars: [],
    },
  });

  const viewer = getValue(data?.viewer, "Viewer");
  const orgPersonQueryResult = getValue(
    viewer?.orgPersonQueryResultErrorable,
    "OrgPersonQueryResult",
  );

  const calendarProfiles =
    orgPersonQueryResult?.persons.map((a) => {
      return {
        primaryCalendar: a?.primaryCalendarId || "",
        profile: a?.profile || null,
        isYou: a?.isYou || false,
      };
    }) || [];

  return { calendarProfiles, loading };
};

const useCalendarProfilesFromActiveEvent = (): QueryResponse => {
  const activeEvent = useReadActiveEvent();
  const { eventDetails, loading } = useEventDetails(
    activeEvent?.externalEventId || "",
    activeEvent?.calendarId || "",
  );

  const calendarProfiles = eventDetails.attendeePeople.map((a) => {
    return {
      primaryCalendar: (a.primaryCalendar as string) || "",
      profile: a.profile,
      isYou: a.isYou,
    };
  });

  return { calendarProfiles, loading };
};

const useCalendarProfilesFromTopCollaborators = (): QueryResponse => {
  // TODO: seems like BE is going to implement a new endpoint to fetch
  // the current user's top collaborators. We can query for that here.
  return { calendarProfiles: [], loading: false };
};

export const useFetchMultiCalendarSelectProfiles = (
  orgId: string,
  selectedCalendarIds: string[],
  query: string,
) => {
  const {
    calendarProfiles: calendarProfilesByCalendarIds,
    loading: loadingByCalendarIds,
  } = useCalendarProfilesByCalendarId(orgId, selectedCalendarIds);

  const {
    calendarProfiles: calendarProfilesFromSearch,
    loading: loadingFromSearch,
  } = useCalendarProfilesFromSearch(orgId, query);

  const {
    calendarProfiles: calendarProfilesFromActiveEvent,
    loading: loadingFromActiveEvent,
  } = useCalendarProfilesFromActiveEvent();

  const {
    calendarProfiles: calendarProfilesFromTopCollaborators,
    loading: loadingFromTopCollaborators,
  } = useCalendarProfilesFromTopCollaborators();

  const loading =
    loadingByCalendarIds ||
    loadingFromSearch ||
    loadingFromActiveEvent ||
    loadingFromTopCollaborators;

  return {
    calendarProfilesByCalendarIds,
    calendarProfilesFromSearch,
    calendarProfilesFromActiveEvent,
    calendarProfilesFromTopCollaborators,
    loading,
  };
};
